/**
 * 5-metric rail on the capability map. Pulls the totals from
 * the graph + cascade.
 */
import { useTranslation } from "react-i18next";

import type { CapabilityCell, CascadeSummary } from "shared/api/types";
import type { CapabilityState } from "twin/capmap/stateMap";

export interface MetricsRailProps {
  cells: CapabilityCell[];
  states: Map<string, CapabilityState>;
  cascade: CascadeSummary | null | undefined;
}

export function MetricsRail({ cells, states, cascade }: MetricsRailProps): JSX.Element {
  const { t } = useTranslation();
  const capsTotal = cells.length;
  const mvbTotal = cells.filter((c) => c.mvb_flag).length;
  const impactedIds = new Set<string>();
  for (const [id, s] of states) {
    if (s === "down" || s === "degraded") impactedIds.add(id);
  }
  const mvbHit = cells.filter((c) => c.mvb_flag && impactedIds.has(c.id)).length;
  const mvbPct = mvbTotal > 0 ? Math.round((mvbHit / mvbTotal) * 100) : 0;

  const ibsTotal = cascade?.mvb_total_count ?? 0;
  const ibsHit = cascade?.mvb_impacted_count ?? 0;
  /* Round 17 B11 -- colour by verdict, not by zero: an estate nothing
     has judged draws its zeroes neutral, not emerald. */
  const calm: "green" | "neutral" =
    !cascade || cascade.estate_assessed === false ? "neutral" : "green";

  return (
    <div data-testid="capmap-metrics" className="capmap-metrics">
      <Metric
        label={t("modules.twin.capmap.metrics.capsTotal")}
        value={`${capsTotal}`}
        tone="brass"
        testid="capmap-metric-caps-total"
      />
      <Metric
        label={t("modules.twin.capmap.metrics.mvbCount")}
        value={`${mvbTotal}`}
        sub={t("modules.twin.capmap.metrics.mvbCountSub", { ibsTotal })}
        tone="brass"
        testid="capmap-metric-mvb-count"
      />
      <Metric
        label={t("modules.twin.capmap.metrics.impacted")}
        value={`${impactedIds.size}`}
        tone={impactedIds.size > 0 ? "red" : calm}
        testid="capmap-metric-impacted"
      />
      <Metric
        label={t("modules.twin.capmap.metrics.mvbIntersection")}
        value={`${mvbHit}/${mvbTotal}`}
        sub={`${mvbPct}%`}
        tone={mvbHit > 0 ? "amber" : mvbTotal === 0 ? "neutral" : calm}
        testid="capmap-metric-mvb-intersection"
      />
      <Metric
        label={t("modules.twin.capmap.metrics.ibsAffected")}
        value={`${ibsHit}/${ibsTotal}`}
        tone={ibsHit > 0 ? "red" : ibsTotal === 0 ? "neutral" : calm}
        testid="capmap-metric-ibs-affected"
      />
    </div>
  );
}

interface MetricProps {
  label: string;
  value: string;
  sub?: string;
  tone: "red" | "amber" | "green" | "brass" | "neutral";
  testid: string;
}

function Metric({ label, value, sub, tone, testid }: MetricProps): JSX.Element {
  const tones: Record<typeof tone, string> = {
    red: "text-rose-500",
    amber: "text-amber-500",
    green: "text-emerald-500",
    brass: "text-brand-brass",
    neutral: "text-slate-300",
  };
  return (
    <div data-testid={testid} className="capmap-metric">
      <div className={`capmap-metric-v ${tones[tone]}`}>
        {value}
        {sub && <small> {sub}</small>}
      </div>
      <div className="capmap-metric-l">{label}</div>
    </div>
  );
}
